import { Alerta, Button, DataList, Dialog, PercentField, Row, Select, Stack, TextField } from "@/components";
import type { CotizacionDetalle, CuotaCotizacion } from "@/lib/api";
import { formatMoneda } from "@/lib/format";
import {
  esAnclaItem,
  esAnclaRubro,
  formatPorcentajeCuota,
  llevaDias,
  MAX_CUOTAS_QUINCENALES,
  OPCIONES_ANCLA,
  textoCuando,
  textoPrevista,
  textoReemplazo,
  TITULO_ATAJO,
  type AnclaForm,
  type TipoAtajo,
} from "@/lib/formaDePago";
import { avisoFacturasDelPlan, pagadoDeCuota } from "@/lib/facturas";
import { useFormaDePago } from "./useFormaDePago";

const ATAJOS: TipoAtajo[] = ["acopio", "anticipo", "quincenales"];
const SEGUN = [
  { valor: "fechas", etiqueta: "En fechas" },
  { valor: "rubro", etiqueta: "Según un rubro del cronograma" },
];

/** Card "Forma de pago" de la ficha de un presupuesto recibido (docs/modulo-cotizaciones.md § Forma de pago). */
export function FormaDePago({ base, detalle, recargar, esAdmin }: { base: string; detalle: CotizacionDetalle; recargar: () => void; esAdmin: boolean }) {
  const t = useFormaDePago(base, detalle, recargar);
  const dlg = t.dialogo;
  const a = t.atajo;
  const porRubro = a.segun === "rubro";

  const monto = (c: CuotaCotizacion) => {
    if (c.monto === null || t.moneda === null) return <span className="text-texto-suave">Sin total</span>;
    return (
      <span>
        {formatMoneda(c.monto, t.moneda)}
        {t.moneda === "ARS" && c.monto_usd !== null && <span className="block text-chico text-texto-suave">{formatMoneda(c.monto_usd, "USD")}</span>}
      </span>
    );
  };

  const columnas = [
    { clave: "concepto", titulo: "Concepto", render: (c: CuotaCotizacion) => c.concepto },
    { clave: "porcentaje", titulo: "%", render: (c: CuotaCotizacion) => formatPorcentajeCuota(c.porcentaje) },
    { clave: "monto", titulo: "Monto", render: monto },
    {
      clave: "pagado",
      titulo: "Pagado",
      render: (c: CuotaCotizacion) => {
        const p = pagadoDeCuota(c);
        if (!p) return <span className="text-texto-suave">Sin facturas</span>;
        return (
          <span>
            {p.pagado}
            <span className="block text-chico text-texto-suave">{p.detalle}</span>
          </span>
        );
      },
    },
    {
      clave: "cuando",
      titulo: "Cuándo",
      render: (c: CuotaCotizacion) => {
        const prevista = textoPrevista(c);
        const texto = textoCuando(c);
        return (
          <span>
            {texto === "Sin fecha" ? <span className="text-texto-suave">Sin fecha</span> : texto}
            {prevista && <span className="block text-chico text-texto-suave">{prevista}</span>}
          </span>
        );
      },
    },
  ];

  const acciones = esAdmin
    ? (c: CuotaCotizacion) => (
        <Row espacio={2}>
          <Button variante="secundario" tamanio="sm" aria-label={`Editar ${c.concepto}`} onClick={() => t.abrirEditar(c)}>
            Editar
          </Button>
          <Button variante="fantasma" tamanio="sm" aria-label={`Sacar ${c.concepto}`} onClick={() => t.abrirBorrar(c)}>
            Sacar
          </Button>
        </Row>
      )
    : undefined;

  return (
    <Stack espacio={3}>
      <Row justificar="entre" envolver espacio={2}>
        <h2 className="text-titulo font-light tracking-tight">Forma de pago</h2>
        {esAdmin && (
          <Row envolver espacio={2}>
            {t.cuotas.length > 0 && (
              <Button variante="fantasma" onClick={t.abrirSacar}>
                Sacar forma de pago
              </Button>
            )}
            <Button onClick={t.abrirNueva}>Agregar pago</Button>
          </Row>
        )}
      </Row>
      {esAdmin && (
        <Row envolver espacio={2}>
          <span className="text-chico text-texto-suave">Armar:</span>
          {ATAJOS.map((tipo) => (
            <Button key={tipo} variante="secundario" tamanio="sm" onClick={() => t.abrirAtajo(tipo)}>
              {TITULO_ATAJO[tipo]}
            </Button>
          ))}
        </Row>
      )}
      <Alerta tono="alerta" texto={t.aviso} />
      {t.sinTotal && t.cuotas.length > 0 && <p className="text-chico text-texto-suave">Los montos aparecen cuando el presupuesto tenga el total cargado.</p>}
      <DataList etiqueta={`Forma de pago del presupuesto de ${detalle.proveedor}`} columnas={columnas} filas={t.cuotas} claveFila={(c) => c.id} acciones={acciones} vacio="Todavía no se cargó la forma de pago." />

      <Dialog abierto={dlg?.tipo === "cuota"} onCambio={(abierto) => !abierto && t.cerrar()} titulo={dlg?.tipo === "cuota" && dlg.cuota ? "Editar pago" : "Agregar pago"}>
        {dlg?.tipo === "cuota" && (
          <form onSubmit={(e) => void t.guardar(e)}>
            <Stack espacio={4}>
              <TextField etiqueta="Concepto" value={t.form.concepto} onChange={(e) => t.cambiar("concepto", e.target.value)} error={t.errores.concepto} placeholder="Anticipo, Cuota 1, Saldo…" />
              <div className="grid gap-3 sm:grid-cols-2">
                <PercentField etiqueta="% del total" valor={t.form.porcentaje} onCambio={(v) => t.cambiar("porcentaje", v)} error={t.errores.porcentaje} />
                <Select etiqueta="Cuándo" opciones={OPCIONES_ANCLA} valor={t.form.ancla} onCambio={(v) => t.cambiar("ancla", v as AnclaForm)} />
                {t.form.ancla === "fecha" && (
                  <TextField etiqueta="Fecha prevista" type="date" value={t.form.fecha} onChange={(e) => t.cambiar("fecha", e.target.value)} ayuda="Si todavía no la sabés, dejala vacía." />
                )}
                {esAnclaRubro(t.form.ancla) && (
                  <Select etiqueta="Rubro" opciones={t.opcionesRubro} valor={t.form.rubroId} onCambio={(v) => t.cambiar("rubroId", v)} error={t.errores.rubroId} placeholder={t.cargandoCronograma ? "Cargando…" : "Elegí el rubro"} />
                )}
                {esAnclaItem(t.form.ancla) && (
                  <Select etiqueta="Ítem" opciones={t.opcionesItem} valor={t.form.itemId} onCambio={(v) => t.cambiar("itemId", v)} error={t.errores.itemId} placeholder={t.cargandoCronograma ? "Cargando…" : "Elegí el ítem"} />
                )}
                {llevaDias(t.form.ancla) && (
                  <TextField etiqueta="Días" inputMode="numeric" value={t.form.dias} onChange={(e) => t.cambiar("dias", e.target.value)} error={t.errores.dias} ayuda="Corridos: 30 = a los 30 días, -5 = 5 días antes." />
                )}
              </div>
              {t.form.ancla !== "fecha" && <p className="text-chico text-texto-suave">La fecha sale del cronograma: si cambia el cronograma, el pago lo sigue.</p>}
              <Alerta texto={t.errorGuardar} />
              <Row justificar="fin" espacio={2}>
                <Button type="button" variante="secundario" onClick={t.cerrar}>
                  Cancelar
                </Button>
                <Button type="submit" cargando={t.guardando}>
                  Guardar
                </Button>
              </Row>
            </Stack>
          </form>
        )}
      </Dialog>

      <Dialog abierto={dlg?.tipo === "atajo"} onCambio={(abierto) => !abierto && t.cerrar()} titulo={TITULO_ATAJO[a.tipo]}>
        {dlg?.tipo === "atajo" && (
          <form onSubmit={(e) => void t.guardarAtajo(e)}>
            <Stack espacio={4}>
              <div className="grid gap-3 sm:grid-cols-2">
                <Select etiqueta="Cuándo" opciones={SEGUN} valor={a.segun} onCambio={(v) => t.cambiarAtajo("segun", v as "fechas" | "rubro")} />
                {porRubro && (
                  <Select etiqueta="Rubro" opciones={t.opcionesRubro} valor={a.rubroId} onCambio={(v) => t.cambiarAtajo("rubroId", v)} error={t.erroresAtajo.rubroId} placeholder={t.cargandoCronograma ? "Cargando…" : "Elegí el rubro"} />
                )}
              </div>
              {a.tipo === "acopio" && (
                <>
                  <p className="text-texto-suave">{porRubro ? "Se paga el 100 % en un solo pago, al empezar el rubro." : "Se paga el 100 % en un solo pago."}</p>
                  {!porRubro && <TextField etiqueta="Fecha del acopio" type="date" value={a.fecha} onChange={(e) => t.cambiarAtajo("fecha", e.target.value)} />}
                </>
              )}
              {a.tipo === "anticipo" && (
                <>
                  <PercentField etiqueta="Anticipo" valor={a.porcentaje} onCambio={(v) => t.cambiarAtajo("porcentaje", v)} error={t.erroresAtajo.porcentaje} ayuda={porRubro ? "Al empezar el rubro; el saldo, el resto, al terminarlo." : "El saldo es el resto."} />
                  {!porRubro && (
                    <div className="grid gap-3 sm:grid-cols-2">
                      <TextField etiqueta="Fecha del anticipo" type="date" value={a.fecha} onChange={(e) => t.cambiarAtajo("fecha", e.target.value)} />
                      <TextField etiqueta="Fecha del saldo" type="date" value={a.fechaSiguiente} onChange={(e) => t.cambiarAtajo("fechaSiguiente", e.target.value)} />
                    </div>
                  )}
                </>
              )}
              {a.tipo === "quincenales" && (
                <>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <PercentField etiqueta="Anticipo" valor={a.porcentaje} onCambio={(v) => t.cambiarAtajo("porcentaje", v)} error={t.erroresAtajo.porcentaje} ayuda={porRubro ? "Al empezar el rubro; 0 si no hay." : "0 si no hay anticipo."} />
                    {!porRubro && <TextField etiqueta="Fecha del anticipo" type="date" value={a.fecha} onChange={(e) => t.cambiarAtajo("fecha", e.target.value)} />}
                    <TextField etiqueta="Cantidad de cuotas" inputMode="numeric" value={a.cantidad} onChange={(e) => t.cambiarAtajo("cantidad", e.target.value)} error={t.erroresAtajo.cantidad} ayuda={porRubro ? `Hasta ${MAX_CUOTAS_QUINCENALES}, cada 15 días desde que empieza el rubro.` : `Hasta ${MAX_CUOTAS_QUINCENALES}.`} />
                    {!porRubro && <TextField etiqueta="Fecha de la primera cuota" type="date" value={a.fechaSiguiente} onChange={(e) => t.cambiarAtajo("fechaSiguiente", e.target.value)} ayuda="Las demás, cada 15 días." />}
                  </div>
                </>
              )}
              {t.previaAtajo.length > 0 && (
                <ul aria-label="Pagos que quedan" className="text-chico text-texto-suave">
                  {t.previaAtajo.map((texto) => (
                    <li key={texto}>{texto}</li>
                  ))}
                </ul>
              )}
              <Alerta tono="alerta" texto={textoReemplazo(t.cuotas.length)} />
              <Alerta tono="alerta" texto={avisoFacturasDelPlan(t.cuotas)} />
              <Alerta texto={t.errorReemplazar} />
              <Row justificar="fin" espacio={2}>
                <Button type="button" variante="secundario" onClick={t.cerrar}>
                  Cancelar
                </Button>
                <Button type="submit" cargando={t.reemplazando}>
                  Armar
                </Button>
              </Row>
            </Stack>
          </form>
        )}
      </Dialog>

      <Dialog abierto={dlg?.tipo === "borrar"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Sacar pago">
        {dlg?.tipo === "borrar" && (
          <Stack espacio={4}>
            <p>{`¿Sacar "${dlg.cuota.concepto}" de la forma de pago?`}</p>
            <Alerta tono="alerta" texto={avisoFacturasDelPlan([dlg.cuota])} />
            <Alerta texto={t.errorBorrar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button variante="peligro" cargando={t.borrando} onClick={() => void t.confirmarBorrar()}>
                Sacar
              </Button>
            </Row>
          </Stack>
        )}
      </Dialog>

      <Dialog abierto={dlg?.tipo === "sacar"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Sacar forma de pago">
        {dlg?.tipo === "sacar" && (
          <Stack espacio={4}>
            <p>{t.cuotas.length === 1 ? "¿Sacar el pago de la forma de pago?" : `¿Sacar los ${t.cuotas.length} pagos de la forma de pago?`}</p>
            <Alerta tono="alerta" texto={avisoFacturasDelPlan(t.cuotas)} />
            <Alerta texto={t.errorReemplazar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button variante="peligro" cargando={t.reemplazando} onClick={() => void t.confirmarSacar()}>
                Sacar
              </Button>
            </Row>
          </Stack>
        )}
      </Dialog>
    </Stack>
  );
}
